import { Col, Container, Input, Row } from "reactstrap";
import Select from "react-select";
import MainLayout from "../../layouts/MainLayout";
import { FaInstagram, FaPhoneAlt, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { IoShareOutline } from "react-icons/io5";
import PropertyCard from "../../components/Cards/PropertyCard";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL, STORAGE_URL } from "../../utils/constants";
import { ReactComponent as EmptyIcon } from '../../assets/images/icons/empty.svg';
import DefaultButton from "../../components/Buttons/Default";
import { getOfficePhoneNumber, getOfficePhoneNumberWithoutCountryCode, getOfficeWhatsAppNumber, handleInstagramLink, handleTikTokLink, tField } from "../../utils/helpers";
import { SelectCustomStyles } from "../../utils/styles";
import { useTranslation } from "react-i18next";
import './styles.css';
import { RiVerifiedBadgeFill } from "react-icons/ri";

function OfficePage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [officeLoading, setOfficeLoading] = useState(true);
  const [office, setOffice] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [properties, setProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [formValues, setFormValues] = useState({
    property_for: '',
    category_id: '',
    city_ids: ''
  });

  const getOffice = () => {
    const cacheKey = `office_data_${params.id}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const parsed = JSON.parse(cachedData);

      if(!parsed.office?.is_office) {
        navigate('/404');
      }

      setOffice(parsed.office);
      setProperties(parsed.properties);
      setTotalProperties(parsed.totalProperties);
      setHasMorePages(parsed.hasMorePages);
      setCurrentPage(parsed.currentPage);
      setStates(parsed.states);
      setOfficeLoading(false);
      setPropertiesLoading(false);
      // Still fetch categories and states if not cached
      getCategories();
      return;
    }

    setOfficeLoading(true);
    setPropertiesLoading(true);

    fetch(API_URL + '/offices/' + params.id, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        if(!data.office?.is_office) {
          navigate('/404');
        }

        const propertiesData = data.properties?.data || [];
        const total = data.properties?.total || 0;
        const morePages = data.properties?.current_page < data.properties?.last_page;
        const page = data.properties?.current_page || 1;

        setOffice(data.office);
        setProperties(propertiesData);
        setTotalProperties(total);
        setHasMorePages(morePages);
        setCurrentPage(page);
        setStates(data.states || []);

        sessionStorage.setItem(cacheKey, JSON.stringify({
          office: data.office,
          properties: propertiesData,
          totalProperties: total,
          hasMorePages: morePages,
          currentPage: page,
          states: data.states || []
        }));

        getCategories();
      }
      setOfficeLoading(false);
      setPropertiesLoading(false);
    })
    .catch(error => {
      console.error('Error fetching office:', error);
      setOfficeLoading(false);
      setPropertiesLoading(false);
    });
  }

  useEffect(() => {
    getOffice();
  }, [params.id]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMorePages) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMorePages, currentPage]);

  const getCategories = () => {
    const cacheKey = 'categories_data';
    const cachedCategories = sessionStorage.getItem(cacheKey);

    if (cachedCategories) {
      setCategories(JSON.parse(cachedCategories));
      return;
    }

    fetch(API_URL + '/categories', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {      
      if (data.success) {        
        setCategories(data.categories);
        sessionStorage.setItem(cacheKey, JSON.stringify(data.categories));
      }
    })
    .catch(error => {
      console.error('Error fetching categories:', error);
    });
  }

  const getProperties = (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setPropertiesLoading(true);
    }

    const searchParams = new URLSearchParams({ ...formValues, page }).toString();

    fetch(API_URL + '/offices/' + params.id + '/search?' + searchParams, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        const newItems = data.properties?.data || [];
        const total = data.properties?.total || 0;
        const morePages = data.properties?.current_page < data.properties?.last_page;
        const page = data.properties?.current_page || 1;

        let updatedProperties;
        if (append) {
          updatedProperties = [...properties, ...newItems];
        } else {
          updatedProperties = newItems;
        }

        setProperties(updatedProperties);
        setTotalProperties(total);
        setHasMorePages(morePages);
        setCurrentPage(page);

        // Update cache so navigating back restores all loaded pages
        const cacheKey = `office_data_${params.id}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          parsed.properties = updatedProperties;
          parsed.totalProperties = total;
          parsed.hasMorePages = morePages;
          parsed.currentPage = page;
          sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
        }
      }
      setPropertiesLoading(false);
      setLoadingMore(false);
    })
    .catch(error => {
      console.error('Error fetching properties:', error);
      setPropertiesLoading(false);
      setLoadingMore(false);
    });
  }

  const loadMore = () => {
    if (!loadingMore && hasMorePages) {
      getProperties(currentPage + 1, true);
    }
  }

  const share = () => {
    if (navigator.share) {
      navigator.share({
        title: office?.name,
        text: 'قم بزيارة المكتب على موقع مشنص',
        url: 'https://mishnis.net/ar/office/' + office?.id
      })
      .then(() => console.log('Share successful'))
      .catch(error => console.error('Error sharing:', error));
    } else {
      alert('المشاركة غير متاحة على هذا المتصفح');
    }
  }

  const search = () => {
    setCurrentPage(1);
    setHasMorePages(false);
    getProperties(1, false);
  }  

  return(
    <MainLayout isTopBar screenTitle={ t('office.title') }>
      {officeLoading ?
        <div className="loader-container mt-3">
          <div className="loader"></div>
        </div>
      :
        <Fragment>
          <section className="bg-primary py-5">
            <Container>
              <div className="text-center">
                <img 
                  src={ office?.profile_picture_path ? STORAGE_URL+office.profile_picture_path : require('../../assets/images/default-office.png') } 
                  height={180} 
                  width={180} 
                  className="rounded-pill mb-3" 
                  alt="" 
                  style={{objectFit: 'cover'}}
                />
                <h5 className="text-white mb-4 d-flex justify-content-center align-items-center gap-2">
                  { office?.office?.name ?? office?.name }
                  {office?.is_verified ?
                    <RiVerifiedBadgeFill size={22} />
                  :
                    null
                  }
                </h5>
                <div className="office-profile-socials d-flex justify-content-center">
                  <a href={"tel:"+getOfficePhoneNumber(office)}>
                    <span className="me-2" dir="ltr">{ getOfficePhoneNumberWithoutCountryCode(office) }</span>
                    <FaPhoneAlt size={20} />
                  </a>
                  {office?.phone_number &&
                    <a href={"https://wa.me/"+getOfficeWhatsAppNumber(office)} target="_blank">
                      <FaWhatsapp size={22} />
                    </a>
                  }
                  {!office?.instagram_link || office?.instagram_link?.trim() !== '' &&
                    <a href={handleInstagramLink(office?.instagram_link) || '#'} target="_blank">
                      <FaInstagram size={22} />
                    </a>
                  }
                  {!office?.tiktok_link || office?.tiktok_link?.trim() !== '' &&
                    <a href={handleTikTokLink(office?.tiktok_link) || '#'} target="_blank">
                      <FaTiktok size={22} />
                    </a>
                  }
                  <a href="#" onClick={share}>
                    <IoShareOutline size={22} />
                  </a>
                </div>
              </div>
            </Container>
          </section>

          <section className="py-4">
            <Container>
              <Row className="justify-content-center">
                <Col md="8">
                  <div className="mb-4">
                    <h4 className="mb-0">
                      {t('office.ads.title')} ({totalProperties})
                    </h4>
                  </div>

                  <div className="office-filters-bar border mb-3 p-3 bg-white">
                    <h5 className="fw-bold">{t('office.ads.search.title')}</h5>
                    <div className="row">
                      <div className="col-md-3 col-6 mb-3">
                        <Input type="select" onChange={(event) => setFormValues({ ...formValues, property_for: event.target.value })}>
                          <option value="">{ t('office.ads.search.fields.purpose.label') }</option>
                          <option value="rent">للايجار</option>
                          <option value="sale">للبيع</option>
                          <option value="exchange">للبدل</option>
                        </Input>
                      </div>

                      <div className="col-md-3 col-6 mb-3">
                        <Input type="select" onChange={(event) => setFormValues({ ...formValues, category_id: event.target.value })}>
                          <option value="">{ t('office.ads.search.fields.category.label') }</option>
                          {categories.map((data, key) => (
                            <option value={data.id} key={key}>{ tField(data, 'name') }</option>
                          ))}
                        </Input>
                      </div>

                      <div className="col-md-4 col-12 mb-3">
                        <Select
                          placeholder={ t('office.ads.search.fields.city.label'  ) }
                          options={states?.map(state => ({
                            label: tField(state, 'name'),
                            options: state.cities.map(city => ({
                              value: city.id,
                              label: tField(city, 'name'),
                            })),
                          }))}
                          onChange={(selectedOptions) => {
                            setFormValues({
                              ...formValues,
                              city_ids: selectedOptions.map(opt => opt.value),
                            });
                          }}
                          styles={SelectCustomStyles}
                          isMulti
                          closeMenuOnSelect={false}
                        />
                      </div>

                      <div className="col-md-2 col-12">
                        <DefaultButton onClick={search} block>{t('office.ads.search.button')}</DefaultButton>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {propertiesLoading ?
                <div className="loader-container">
                  <div className="loader"></div>
                </div>
              :
                <>
                  <Row className="justify-content-center">
                    {properties?.length > 0 ?
                      properties.map((data, key) => {
                        return(
                          <Col md="8" key={key}>
                            <PropertyCard property={data} />
                          </Col>
                        );
                      })
                    :
                      <Col md="12" className="text-center py-5">
                        <EmptyIcon />
                        <h6 className="mt-4 fw-normal text-muted">لا توجد عقارات</h6>
                      </Col>
                    }
                  </Row>

                  {loadingMore && (
                    <Row className="justify-content-center my-4">
                      <Col lg="12" className="text-center">
                        <div className="loader mx-auto"></div>
                      </Col>
                    </Row>
                  )}
                </>
              }
            </Container>
          </section>
        </Fragment>
      }
    </MainLayout>
  );
}

export default OfficePage;